import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Loader2, Save } from 'lucide-react';
import { SaveButton } from '@/components/ui/SaveButton';
import { useAdminProducerContext } from '@/context/AdminProducerContext';
import { toast } from '@/hooks/use-toast';
import {
  BasicInfoSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { useProducerFormData } from '@/hooks/useProducerFormData';

export default function AdminProducerGrundinfo() {
  const { producerId } = useParams<{ producerId: string }>();
  const navigate = useNavigate();
  const { getProducerById, updateProducer } = useAdminProducerContext();
  const [producer, setProducer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLongDescription, setShowLongDescription] = useState(false);

  useEffect(() => {
    const loadProducer = async () => {
      if (!producerId) return;
      setIsLoading(true);
      try {
        const data = await getProducerById(producerId);
        setProducer(data || null);
      } catch (err) {
        console.error('Failed to load producer:', err);
        setProducer(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducer();
  }, [producerId, getProducerById]);

  const { initialFormData, mapFormStatusToFarmStatus } = useProducerFormData(producer, false, producerId);

  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async (data) => {
      if (!producer) return;
      await updateProducer(producer.id, {
        name: data.name,
        shortDescription: data.shortDescription,
        longDescription: data.longDescription || undefined,
      });
      toast({ title: 'Sparat', description: 'Grundinfo har uppdaterats.' });
    },
  });

  useEffect(() => {
    if (editorState.formData.longDescription) {
      setShowLongDescription(true);
    }
  }, [editorState.formData.longDescription]);

  if (isLoading) {
    return (
      <AdminLayout producerId={producerId}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (!producer) {
    return (
      <AdminLayout producerId={producerId}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Värden hittades inte.</AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout producerId={producerId} producerName={producer.name} title="Grundinfo">
      {/* Section header with save button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Grundinfo</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Namn och beskrivning av värden.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {editorState.isDirty && (
            <span className="text-xs text-amber-600 font-medium">Osparade ändringar</span>
          )}
          <Badge variant={producer.status === 'public' ? 'default' : 'secondary'}>
            {producer.status === 'public' ? 'Publicerad' : 'Utkast'}
          </Badge>
          <SaveButton
            size="sm"
            onClick={editorState.save}
            disabled={!editorState.isDirty}
            isSaving={editorState.isSaving}
          >
            <Save className="h-4 w-4 mr-2" />
            Spara
          </SaveButton>
        </div>
      </div>

      <div className="max-w-2xl">
        <BasicInfoSection
          formData={editorState.formData}
          errors={editorState.errors}
          updateField={editorState.updateField}
          showLongDescription={showLongDescription}
          onToggleLongDescription={setShowLongDescription}
          producerId={producer.id}
        />
      </div>
    </AdminLayout>
  );
}
